import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, finalize, map, of, shareReplay, tap } from 'rxjs';
import { ApiResponse, AuthService } from './auth.service';

/**
 * Single owner of the sidebar menu / permission tree.
 *
 * Every feature route wraps itself in its own AppLayout, so the sidebar (AppMenu) is rebuilt on
 * almost every navigation. Components must therefore never call the menu API themselves - they read
 * from here, and this service hits the API only when nothing usable is cached.
 *
 * Cache layers: in-memory (this service) -> localStorage ('menuList', also read directly by
 * permissionGuard / PermissionService) -> API. A cached menu is bound to the user it was fetched for
 * and goes stale after TTL_MS, so role changes made elsewhere still show up.
 */
@Injectable({
    providedIn: 'root'
})
export class MenuService {
    private static readonly TTL_MS = 5 * 60 * 1000;
    private static readonly MENU_KEY = 'menuList';
    private static readonly USER_KEY = 'menuListUserId';
    private static readonly LOADED_AT_KEY = 'menuListLoadedAt';

    private readonly menuSubject = new BehaviorSubject<any[]>([]);
    /** Current menu tree; emits immediately with whatever is cached (empty until first load). */
    readonly menu$ = this.menuSubject.asObservable();

    private loadedForUser: string | null = null;
    private loadedAt = 0;
    private inFlight: { userId: string; request$: Observable<any[]> } | null = null;

    constructor(private readonly authService: AuthService) {
        this.hydrateFromStorage();

        this.authService.rolesChanged$.subscribe(() => this.invalidate());

        // Drop the cache on logout / token expiry so the next user never sees the previous menu.
        this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
            if (!isAuthenticated) {
                this.clear();
            }
        });
    }

    get menu(): any[] {
        return this.menuSubject.value;
    }

    /** Make sure the menu for the logged-in user is loaded; refreshes in the background when stale. */
    ensureLoaded(): void {
        if (!this.currentUserId()) {
            return;
        }
        if (!this.hasFreshMenu()) {
            this.refresh().subscribe({ error: (err) => console.error('Failed to load menu list:', err) });
        }
    }

    /**
     * Menu for the logged-in user: the cached copy when there is one for this user (even if stale,
     * so permission checks never wait on the network), otherwise one shared API call.
     */
    load(): Observable<any[]> {
        if (this.hasMenuForCurrentUser()) {
            return of(this.menu);
        }
        return this.refresh();
    }

    /** Force a fetch (login, role change). Concurrent calls for the same user share one request. */
    refresh(userId?: string): Observable<any[]> {
        const id = userId || this.currentUserId();
        if (!id) {
            return of([]);
        }
        if (this.inFlight?.userId === id) {
            return this.inFlight.request$;
        }

        const request$ = this.authService.getMenuList(id).pipe(
            map((response: ApiResponse<any>) => {
                if (!response.isSuccess) {
                    throw response.errors;
                }
                return (response.data ?? []) as any[];
            }),
            tap((data) => this.store(id, data)),
            finalize(() => {
                if (this.inFlight?.request$ === request$) {
                    this.inFlight = null;
                }
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        this.inFlight = { userId: id, request$ };
        return request$;
    }

    /** Mark the cache stale (e.g. after editing roles) so the next layout/menu load refetches. */
    invalidate(): void {
        this.loadedAt = 0;
        try {
            localStorage.removeItem(MenuService.LOADED_AT_KEY);
        } catch {
            // storage unavailable - the in-memory flag above is enough
        }
    }

    clear(): void {
        this.loadedForUser = null;
        this.loadedAt = 0;
        this.inFlight = null;
        if (this.menuSubject.value.length) {
            this.menuSubject.next([]);
        }
    }

    private store(userId: string, data: any[]): void {
        this.loadedForUser = userId;
        this.loadedAt = Date.now();
        this.menuSubject.next(data);
        try {
            localStorage.setItem(MenuService.MENU_KEY, JSON.stringify(data));
            localStorage.setItem(MenuService.USER_KEY, userId);
            localStorage.setItem(MenuService.LOADED_AT_KEY, String(this.loadedAt));
        } catch (error) {
            console.error('Could not persist menu list:', error);
        }
    }

    private hydrateFromStorage(): void {
        try {
            const userId = this.currentUserId();
            const cachedUser = localStorage.getItem(MenuService.USER_KEY);
            const raw = localStorage.getItem(MenuService.MENU_KEY);
            if (!userId || !raw || cachedUser !== userId) {
                return;
            }
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                this.loadedForUser = userId;
                this.loadedAt = Number(localStorage.getItem(MenuService.LOADED_AT_KEY)) || 0;
                this.menuSubject.next(parsed);
            }
        } catch {
            // corrupt/unavailable storage - fall back to fetching
        }
    }

    private hasMenuForCurrentUser(): boolean {
        const userId = this.currentUserId();
        return !!userId && this.loadedForUser === userId && this.menu.length > 0;
    }

    private hasFreshMenu(): boolean {
        return this.hasMenuForCurrentUser() && Date.now() - this.loadedAt < MenuService.TTL_MS;
    }

    private currentUserId(): string | null {
        try {
            return localStorage.getItem('userId');
        } catch {
            return null;
        }
    }
}
